import React, {
  useEffect,
  useMemo,
  useRef,
  useState,
} from "react";

import {
  FiChevronLeft,
  FiChevronRight,
  FiImage,
  FiPlus,
  FiStar,
  FiTrash2,
  FiUploadCloud,
} from "react-icons/fi";

import api from "../services/api";

const MAX_FILES_PER_UPLOAD = 10;
const MAX_FILE_SIZE = 12 * 1024 * 1024;

const getLegacyUrls = (value) => {
  if (!value) return [];

  return String(value)
    .split(",")
    .map((url) => url.trim())
    .filter(Boolean);
};

const AppointmentImageManager = ({
  appointmentId,
  projectId,
  initialImages = [],
  legacyImageUrl = "",
  onImagesChange,
  onOpenImages,
}) => {
  const imageBaseUrl = projectId ? `/api/tattoo-projects/${projectId}/images` : `/api/appointments/${appointmentId}/images`;
  const inputRef = useRef(null);

  const [images, setImages] = useState(
    Array.isArray(initialImages)
      ? initialImages
      : []
  );

  const [isDragging, setIsDragging] =
    useState(false);

  const [isUploading, setIsUploading] =
    useState(false);

  const [uploadProgress, setUploadProgress] =
    useState(0);

  const [errorMessage, setErrorMessage] =
    useState("");

  const legacyUrls = useMemo(
    () => getLegacyUrls(legacyImageUrl),
    [legacyImageUrl]
  );

  useEffect(() => {
    setImages(
      Array.isArray(initialImages)
        ? initialImages
        : []
    );
  }, [initialImages]);

  const notifyChange = (nextImages) => {
    setImages(nextImages);
    onImagesChange?.(nextImages);
  };

  const getAllViewerUrls = () => [
    ...images.map((image) => image.url),
    ...legacyUrls,
  ];

  const validateFiles = (selectedFiles) => {
    const files = Array.from(selectedFiles || []);

    if (files.length === 0) {
      return [];
    }

    if (files.length > MAX_FILES_PER_UPLOAD) {
      throw new Error(
        `Bitte maximal ${MAX_FILES_PER_UPLOAD} Bilder gleichzeitig auswählen.`
      );
    }

    const oversizedFile = files.find(
      (file) => file.size > MAX_FILE_SIZE
    );

    if (oversizedFile) {
      throw new Error(
        `${oversizedFile.name} ist größer als 12 MB.`
      );
    }

    const invalidFile = files.find(
      (file) =>
        !file.type.startsWith("image/") &&
        !/\.(heic|heif)$/i.test(file.name)
    );

    if (invalidFile) {
      throw new Error(
        `${invalidFile.name} ist kein unterstütztes Bild.`
      );
    }

    return files;
  };

  const uploadFiles = async (selectedFiles) => {
    if ((!appointmentId && !projectId) || isUploading) {
      return;
    }

    setErrorMessage("");

    let files;

    try {
      files = validateFiles(selectedFiles);
    } catch (error) {
      setErrorMessage(error.message);
      return;
    }

    if (files.length === 0) {
      return;
    }

    const formData = new FormData();

    files.forEach((file) => {
      formData.append("images", file);
    });

    try {
      setIsUploading(true);
      setUploadProgress(0);

      const response = await api.post(
        imageBaseUrl,
        formData,
        {
          onUploadProgress: (event) => {
            if (!event.total) return;

            setUploadProgress(
              Math.round(
                (event.loaded * 100) /
                  event.total
              )
            );
          },
        }
      );

      const uploadedImages = Array.isArray(
        response.data?.images
      )
        ? response.data.images
        : [];

      notifyChange([
        ...images,
        ...uploadedImages,
      ]);

      setUploadProgress(100);
    } catch (error) {
      console.error(
        "Bild-Upload fehlgeschlagen:",
        error
      );

      const status = error.response?.status;
      const fallback = status === 413
        ? "Der Server hat den Upload wegen der Dateigröße abgelehnt. Bitte ein kleineres Bild oder die Bilder einzeln hochladen."
        : status === 401
          ? "Deine Anmeldung ist abgelaufen. Bitte erneut anmelden und den Upload wiederholen."
          : status === 403
            ? "Du hast keine Berechtigung für diesen Bild-Upload."
            : status === 404
              ? "Das Projekt oder der Termin wurde nicht gefunden. Bitte neu laden."
              : "Die Bilder konnten nicht hochgeladen werden.";
      setErrorMessage(
        `${error.response?.data?.message || fallback}${status ? ` (HTTP ${status})` : " Bitte die Verbindung prüfen."}`
      );
    } finally {
      setIsUploading(false);

      if (inputRef.current) {
        inputRef.current.value = "";
      }
    }
  };

  const deleteImage = async (image) => {
    const confirmed = window.confirm(
      "Dieses Bild wirklich löschen?"
    );

    if (!confirmed) {
      return;
    }

    setErrorMessage("");

    try {
      await api.delete(
        `/api/project-images/${image.id}`
      );

      const nextImages = images.filter(
        (item) => item.id !== image.id
      );

      if (
        nextImages.length > 0 &&
        !nextImages.some(
          (item) => item.isCover
        )
      ) {
        nextImages[0] = {
          ...nextImages[0],
          isCover: true,
        };
      }

      notifyChange(nextImages);
    } catch (error) {
      console.error(
        "Bild konnte nicht gelöscht werden:",
        error
      );

      setErrorMessage(
        error.response?.data?.message ||
          "Das Bild konnte nicht gelöscht werden."
      );
    }
  };

  const saveOrder = async (nextImages) => {
    const normalizedImages =
      nextImages.map((image, index) => ({
        ...image,
        sortOrder: index,
        isCover: index === 0,
      }));

    notifyChange(normalizedImages);

    try {
      await api.put(
        `${imageBaseUrl}/order`,
        {
          imageIds: normalizedImages.map(
            (image) => image.id
          ),
        }
      );
    } catch (error) {
      console.error(
        "Bildreihenfolge konnte nicht gespeichert werden:",
        error
      );

      setErrorMessage(
        error.response?.data?.message ||
          "Die Reihenfolge konnte nicht gespeichert werden."
      );

      setImages(images);
      onImagesChange?.(images);
    }
  };

  const moveImage = (index, direction) => {
    const targetIndex = index + direction;

    if (
      targetIndex < 0 ||
      targetIndex >= images.length
    ) {
      return;
    }

    const nextImages = [...images];

    [
      nextImages[index],
      nextImages[targetIndex],
    ] = [
      nextImages[targetIndex],
      nextImages[index],
    ];

    saveOrder(nextImages);
  };

  const makeCover = (index) => {
    if (index === 0) return;

    const nextImages = [...images];
    const [selectedImage] =
      nextImages.splice(index, 1);

    nextImages.unshift(selectedImage);

    saveOrder(nextImages);
  };

  return (
    <section className="project-image-manager">
      <div className="project-image-manager-header">
        <div>
          <h4>Projektbilder</h4>

          <p>
            Das erste Bild wird als Titelbild
            verwendet.
          </p>
        </div>

        <span className="project-image-count">
          {images.length + legacyUrls.length}
        </span>
      </div>

      <input
        ref={inputRef}
        type="file"
        accept="image/jpeg,image/png,image/webp,image/heic,image/heif,.heic,.heif"
        multiple
        hidden
        onChange={(event) =>
          uploadFiles(event.target.files)
        }
      />

      <button
        type="button"
        className={`project-image-dropzone ${
          isDragging ? "is-dragging" : ""
        }`}
        disabled={isUploading}
        onClick={() =>
          inputRef.current?.click()
        }
        onDragEnter={(event) => {
          event.preventDefault();
          setIsDragging(true);
        }}
        onDragOver={(event) => {
          event.preventDefault();
          setIsDragging(true);
        }}
        onDragLeave={(event) => {
          event.preventDefault();

          if (
            !event.currentTarget.contains(
              event.relatedTarget
            )
          ) {
            setIsDragging(false);
          }
        }}
        onDrop={(event) => {
          event.preventDefault();
          setIsDragging(false);
          uploadFiles(event.dataTransfer.files);
        }}
      >
        {isUploading ? (
          <>
            <FiUploadCloud />
            <strong>
              Bilder werden optimiert …
            </strong>
            <span>{uploadProgress}%</span>
          </>
        ) : (
          <>
            <FiPlus />
            <strong>Bilder hinzufügen</strong>
            <span>
              Auswählen oder hierher ziehen
            </span>
          </>
        )}

        {isUploading && (
          <span className="project-upload-progress">
            <span
              style={{
                width: `${uploadProgress}%`,
              }}
            />
          </span>
        )}
      </button>

      {errorMessage && (
        <div
          className="project-image-error"
          role="alert"
        >
          {errorMessage}
        </div>
      )}

      {images.length > 0 && (
        <div className="project-image-grid">
          {images.map((image, index) => (
            <article
              key={image.id}
              className="project-image-item"
            >
              <button
                type="button"
                className="project-image-preview"
                onClick={() =>
                  onOpenImages?.(
                    getAllViewerUrls(),
                    index
                  )
                }
              >
                <img
                  src={image.thumbnailUrl}
                  alt={
                    image.originalName ||
                    `Projektbild ${index + 1}`
                  }
                  loading="lazy"
                />

                {index === 0 && (
                  <span className="project-cover-badge">
                    <FiStar />
                    Titelbild
                  </span>
                )}
              </button>

              <div className="project-image-actions">
                <button
                  type="button"
                  disabled={index === 0}
                  onClick={() =>
                    moveImage(index, -1)
                  }
                  title="Nach links"
                  aria-label="Bild nach links"
                >
                  <FiChevronLeft />
                </button>

                <button
                  type="button"
                  disabled={
                    index === images.length - 1
                  }
                  onClick={() =>
                    moveImage(index, 1)
                  }
                  title="Nach rechts"
                  aria-label="Bild nach rechts"
                >
                  <FiChevronRight />
                </button>

                <button
                  type="button"
                  disabled={index === 0}
                  onClick={() =>
                    makeCover(index)
                  }
                  title="Als Titelbild verwenden"
                  aria-label="Als Titelbild verwenden"
                >
                  <FiStar />
                </button>

                <button
                  type="button"
                  className="project-image-delete"
                  onClick={() =>
                    deleteImage(image)
                  }
                  title="Bild löschen"
                  aria-label="Bild löschen"
                >
                  <FiTrash2 />
                </button>
              </div>
            </article>
          ))}
        </div>
      )}

      {legacyUrls.length > 0 && (
        <div className="legacy-project-images">
          <div className="legacy-project-images-label">
            <FiImage />
            Bilder aus der ursprünglichen Anfrage
          </div>

          <div className="project-image-grid">
            {legacyUrls.map((url, index) => (
              <article
                key={`${url}-${index}`}
                className="project-image-item is-legacy"
              >
                <button
                  type="button"
                  className="project-image-preview"
                  onClick={() =>
                    onOpenImages?.(
                      getAllViewerUrls(),
                      images.length + index
                    )
                  }
                >
                  <img
                    src={url}
                    alt={`Anfragebild ${index + 1}`}
                    loading="lazy"
                  />

                  <span className="project-legacy-badge">
                    Anfrage
                  </span>
                </button>
              </article>
            ))}
          </div>
        </div>
      )}
    </section>
  );
};

export default AppointmentImageManager;
